import { useState, useEffect, useCallback, useRef } from "react";
import { format } from "date-fns";
import type { FormFieldConfig } from "./public/EOCForm";

interface EOCRecord {
  _id: string;
  dateCallWasTaken?: string;
  prospectName?: string;
  prospectEmail?: string;
  prospectNumber?: string;
  closerName?: string;
  setterName?: string;
  offerPitched?: string;
  callOutcome?: string;
  cashCollected?: number;
  revenueGenerated?: number;
  paymentMethod?: string;
  paymentPlan?: string;
  customPaymentPlanDetails?: string;
  callNotes?: string;
  leadSource?: string;
  sourcePlatform?: string;
  utmMedium?: string;
  utmContent?: string;
  utmSource?: string;
  submittedAt?: string;
  timestamp?: string;
}

const OUTCOME_COLORS: Record<string, string> = {
  closed: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200/50 dark:border-emerald-500/30",
  "no show": "bg-red-500/10 text-red-600 dark:text-red-400 border-red-200/50 dark:border-red-500/30",
  "not qualified": "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-200/50 dark:border-orange-500/30",
  "follow up": "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-200/50 dark:border-blue-500/30",
  "nurture": "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-200/50 dark:border-purple-500/30",
};

function getOutcomeClass(outcome?: string): string {
  if (!outcome) return "bg-gray-500/10 text-gray-600 border-gray-200/50";
  const key = Object.keys(OUTCOME_COLORS).find((k) =>
    outcome.toLowerCase().startsWith(k)
  );
  return key
    ? OUTCOME_COLORS[key]
    : "bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-200/50 dark:border-gray-500/30";
}

function formatCurrency(val?: number): string {
  if (!val) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(val);
}

// ============================================================================
// SHARE FORM CARD
// ============================================================================

function ShareFormCard() {
  const formUrl = `${window.location.origin}/eoc`;
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(formUrl);
    setCopied(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-2xl bg-gradient-to-r from-[var(--brand-gradient-start)]/10 to-[var(--brand-gradient-end)]/10 border border-[var(--brand-primary)]/20 p-5 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-4 min-w-0">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-foreground text-sm">Closer EOC Form</p>
          <p className="text-xs text-muted-foreground mt-0.5">Share this link with your team — no login required</p>
          <p className="text-xs text-[var(--brand-primary)] font-mono mt-1 truncate">{formUrl}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <a
          href={formUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 rounded-xl border border-[var(--brand-primary)]/30 text-[var(--brand-primary)] text-sm font-medium hover:bg-[var(--brand-primary)]/10 transition-colors"
        >
          Open Form
        </a>
        <button
          onClick={handleCopy}
          className="px-4 py-2 rounded-xl bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white text-sm font-medium hover:shadow-lg hover:shadow-[var(--brand-primary)]/25 transition-all flex items-center gap-2"
        >
          {copied ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy Link
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// TEAM MANAGEMENT SECTION (closers + setters)
// ============================================================================

function NameList({
  title,
  names,
  onAdd,
  onRemove,
}: {
  title: string;
  names: string[];
  onAdd: (name: string) => void;
  onRemove: (name: string) => void;
}) {
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleAdd = () => {
    const trimmed = input.trim();
    if (!trimmed || names.includes(trimmed)) return;
    onAdd(trimmed);
    setInput("");
    inputRef.current?.focus();
  };

  return (
    <div className="flex-1 min-w-[200px]">
      <h4 className="text-sm font-semibold text-foreground mb-3">{title}</h4>
      <div className="flex gap-2 mb-3">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }}
          placeholder={`Add ${title.toLowerCase()}...`}
          className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary)]/20 outline-none transition-all text-sm"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!input.trim()}
          className="px-3 py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Add
        </button>
      </div>
      {names.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">No {title.toLowerCase()} added yet.</p>
      ) : (
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {names.map(name => (
            <div key={name} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/40 group">
              <span className="text-sm text-foreground truncate">{name}</span>
              <button
                type="button"
                onClick={() => onRemove(name)}
                className="ml-2 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500 transition-all flex-shrink-0"
                title={`Remove ${name}`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EOCTeamSection() {
  const [closers, setClosers] = useState<string[]>([]);
  const [setters, setSetters] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch("/api/eoc/config")
      .then(r => r.json())
      .then((d: { closers?: string[]; setters?: string[] }) => {
        setClosers(d.closers || []);
        setSetters(d.setters || []);
      })
      .catch(console.error);
  }, []);

  const updateList = async (type: "closers" | "setters", action: "add" | "remove", name: string) => {
    setSaving(true);
    try {
      await fetch(`/api/eoc/config/${type}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, name }),
      });
      if (type === "closers") {
        setClosers(prev => action === "add" ? [...prev, name] : prev.filter(n => n !== name));
      } else {
        setSetters(prev => action === "add" ? [...prev, name] : prev.filter(n => n !== name));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Team Members</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Manage closer and setter dropdown lists shown in the EOC form</p>
        </div>
        {saving && (
          <div className="w-4 h-4 border-2 border-[var(--brand-primary)]/20 border-t-[var(--brand-primary)] rounded-full animate-spin" />
        )}
      </div>
      <div className="p-6 flex gap-8 flex-wrap">
        <NameList
          title="Closers"
          names={closers}
          onAdd={name => updateList("closers", "add", name)}
          onRemove={name => updateList("closers", "remove", name)}
        />
        <NameList
          title="Setters"
          names={setters}
          onAdd={name => updateList("setters", "add", name)}
          onRemove={name => updateList("setters", "remove", name)}
        />
      </div>
    </div>
  );
}

// ============================================================================
// EOC FORM BUILDER MODAL
// ============================================================================

const FIELD_TYPES: FormFieldConfig["type"][] = ["text", "number", "date", "textarea", "select"];
const TEAM_KEYS = new Set(["closerName", "setterName"]);

const FIELD_LABELS: Record<string, string> = {
  dateCallWasTaken: "Date Call Was Taken",
  prospectName: "Prospect Full Name",
  prospectNumber: "Prospect Phone",
  prospectEmail: "Prospect Email",
  closerName: "Closer Name",
  setterName: "Setter Name",
  offerPitched: "Offer Pitched?",
  callOutcome: "Call Outcome",
  cashCollected: "Cash Collected ($)",
  revenueGenerated: "Revenue Generated ($)",
  paymentMethod: "Payment Method",
  paymentPlan: "Payment Plan",
  customPaymentPlanDetails: "Custom Payment Plan Details",
  leadSource: "New Lead or Referral?",
  callNotes: "Call Notes",
};

interface FieldEditorRowProps {
  field: FormFieldConfig;
  onChange: (updated: FormFieldConfig) => void;
}

function FieldEditorRow({ field, onChange }: FieldEditorRowProps) {
  const [optionInput, setOptionInput] = useState("");
  const isTeam = TEAM_KEYS.has(field.key);

  const addOption = () => {
    const trimmed = optionInput.trim();
    if (!trimmed || field.options?.includes(trimmed)) return;
    onChange({ ...field, options: [...(field.options || []), trimmed] });
    setOptionInput("");
  };

  const removeOption = (opt: string) => {
    onChange({ ...field, options: (field.options || []).filter(o => o !== opt) });
  };

  return (
    <div className={`rounded-xl border transition-colors ${field.enabled ? "border-border bg-card" : "border-border/40 bg-muted/20 opacity-60"}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Enable toggle */}
        <button
          type="button"
          onClick={() => onChange({ ...field, enabled: !field.enabled })}
          className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
            field.enabled
              ? "bg-[var(--brand-primary)]/15 text-[var(--brand-primary)]"
              : "bg-muted text-muted-foreground"
          }`}
          title={field.enabled ? "Click to disable" : "Click to enable"}
        >
          {field.enabled ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
        </button>

        {/* Label */}
        <input
          type="text"
          value={field.label}
          onChange={e => onChange({ ...field, label: e.target.value })}
          className="flex-1 min-w-0 bg-transparent text-sm text-foreground font-medium border-b border-transparent focus:border-[var(--brand-primary)] outline-none transition-colors py-0.5"
          placeholder={FIELD_LABELS[field.key] || field.key}
        />

        {/* Type selector */}
        {!isTeam && (
          <select
            value={field.type}
            onChange={e => onChange({ ...field, type: e.target.value as FormFieldConfig["type"] })}
            className="text-xs px-2 py-1.5 rounded-lg border border-border bg-background text-muted-foreground focus:border-[var(--brand-primary)] outline-none transition-colors cursor-pointer"
          >
            {FIELD_TYPES.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        )}

        {/* Required badge */}
        <button
          type="button"
          onClick={() => onChange({ ...field, required: !field.required })}
          className={`text-xs px-2 py-1 rounded-md font-medium transition-colors flex-shrink-0 ${
            field.required
              ? "bg-red-500/10 text-red-500 hover:bg-red-500/20"
              : "bg-muted text-muted-foreground hover:bg-muted/80"
          }`}
          title="Toggle required"
        >
          {field.required ? "required" : "optional"}
        </button>
      </div>

      {/* Options editor (only for select type, excluding team fields) */}
      {field.type === "select" && !isTeam && (
        <div className="px-4 pb-3 pt-1 border-t border-border/50">
          <p className="text-xs text-muted-foreground mb-2 font-medium">Dropdown options</p>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(field.options || []).map(opt => (
              <span key={opt} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted text-xs text-foreground">
                {opt}
                <button
                  type="button"
                  onClick={() => removeOption(opt)}
                  className="text-muted-foreground hover:text-red-500 transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
            {(field.options || []).length === 0 && (
              <span className="text-xs text-muted-foreground italic">No options yet</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={optionInput}
              onChange={e => setOptionInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addOption(); } }}
              placeholder="Add option..."
              className="flex-1 px-2.5 py-1.5 text-xs rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary)]/20 outline-none transition-all"
            />
            <button
              type="button"
              onClick={addOption}
              disabled={!optionInput.trim()}
              className="px-2.5 py-1.5 rounded-lg bg-[var(--brand-primary)] text-white text-xs font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {/* Team key note */}
      {isTeam && (
        <div className="px-4 pb-3 pt-1 border-t border-border/50">
          <p className="text-xs text-muted-foreground">Options come from the Team Members list above</p>
        </div>
      )}
    </div>
  );
}

const DEFAULT_FIELDS: FormFieldConfig[] = [
  { key: "dateCallWasTaken", label: "Date Call Was Taken", type: "date", required: true, enabled: true },
  { key: "prospectName", label: "Prospect Full Name", type: "text", required: true, enabled: true },
  { key: "prospectNumber", label: "Prospect Phone", type: "text", enabled: true },
  { key: "prospectEmail", label: "Prospect Email", type: "text", required: true, enabled: true },
  { key: "closerName", label: "Closer Name", type: "select", required: true, enabled: true, options: [] },
  { key: "setterName", label: "Setter Name", type: "select", enabled: true, options: [] },
  { key: "offerPitched", label: "Offer Pitched?", type: "select", enabled: true, options: ["Yes", "No"] },
  { key: "callOutcome", label: "Call Outcome", type: "select", required: true, enabled: true, options: ["Closed", "Deposit Collected", "Upsell", "Downsell", "Taken", "Not Closed", "Follow-up Scheduled", "Rescheduled", "No-show", "Cancelled"] },
  { key: "cashCollected", label: "Cash Collected ($)", type: "number", enabled: true },
  { key: "revenueGenerated", label: "Revenue Generated ($)", type: "number", enabled: true },
  { key: "paymentMethod", label: "Payment Method", type: "select", enabled: true, options: ["Credit Card", "Bank Transfer", "Cash", "WOP", "Fan Basis", "Other"] },
  { key: "paymentPlan", label: "Payment Plan", type: "select", enabled: true, options: ["Pay in Full", "$1k/month for 4 months", "$2k/month for 2 months", "Custom"] },
  { key: "customPaymentPlanDetails", label: "Custom Payment Plan Details", type: "text", enabled: true },
  { key: "leadSource", label: "New Lead or Referral?", type: "select", enabled: true, options: ["New Lead", "Referral"] },
  { key: "callNotes", label: "Call Notes", type: "textarea", enabled: true },
];

interface EOCFormBuilderModalProps {
  onClose: () => void;
}

function EOCFormBuilderModal({ onClose }: EOCFormBuilderModalProps) {
  const [fields, setFields] = useState<FormFieldConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetch("/api/eoc/config")
      .then(r => r.json())
      .then((d: { formFields?: FormFieldConfig[] }) => {
        const saved = d.formFields || [];
        // Merge saved config over defaults (preserve order of defaults, apply saved overrides)
        const merged = DEFAULT_FIELDS.map(def => {
          const override = saved.find(s => s.key === def.key);
          return override ? { ...def, ...override } : def;
        });
        setFields(merged);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const updateField = (key: string, updated: FormFieldConfig) => {
    setFields(prev => prev.map(f => f.key === key ? updated : f));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch("/api/eoc/config/form", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fields }),
      });
      const data = await res.json() as { success: boolean };
      if (data.success) setSaved(true);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFields(DEFAULT_FIELDS.map(f => ({ ...f })));
    setSaved(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col bg-card rounded-2xl shadow-2xl border border-border overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-foreground">Edit EOC Form</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Customize field labels, types, and dropdown options</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Field list */}
        <div className="flex-1 overflow-y-auto p-6 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-[var(--brand-primary)]/20 border-t-[var(--brand-primary)] rounded-full animate-spin" />
            </div>
          ) : (
            fields.map(field => (
              <FieldEditorRow
                key={field.key}
                field={field}
                onChange={updated => updateField(field.key, updated)}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border flex-shrink-0 gap-3">
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2 rounded-xl text-sm text-muted-foreground border border-border hover:bg-muted transition-colors"
          >
            Reset to defaults
          </button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm text-muted-foreground border border-border hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white hover:shadow-lg hover:shadow-[var(--brand-primary)]/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : saved ? (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved!
                </>
              ) : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN EOCReports COMPONENT
// ============================================================================

export function EOCReports() {
  const [records, setRecords] = useState<EOCRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [formBuilderOpen, setFormBuilderOpen] = useState(false);
  const PAGE_SIZE = 25;

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      if (search) params.set("search", search);
      const res = await fetch(`/api/eoc/reports?${params}`);
      const data = await res.json() as { success: boolean; data: EOCRecord[]; total: number; error?: string };
      if (!data.success) throw new Error(data.error || "Failed to load");
      setRecords(data.data);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load EOC reports");
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPage(1);
    fetchRecords();
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">EOC Reports</h2>
          <p className="text-muted-foreground mt-1">End-of-call submissions from your closer team</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] text-sm font-medium">
            {total} total
          </div>
          <button
            onClick={() => setFormBuilderOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[var(--brand-primary)]/30 text-[var(--brand-primary)] text-sm font-medium hover:bg-[var(--brand-primary)]/10 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit Form
          </button>
        </div>
      </div>

      {/* Team + Share row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EOCTeamSection />
        <div className="flex flex-col gap-4">
          <ShareFormCard />
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by prospect, closer, setter, outcome..."
          className="flex-1 px-4 py-2.5 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary)]/20 outline-none transition-all text-sm"
        />
        <button
          type="submit"
          className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white font-medium text-sm hover:shadow-lg hover:shadow-[var(--brand-primary)]/25 transition-all"
        >
          Search
        </button>
        {search && (
          <button
            type="button"
            onClick={() => { setSearch(""); setPage(1); }}
            className="px-4 py-2.5 rounded-xl border border-border text-muted-foreground hover:bg-secondary transition-colors text-sm"
          >
            Clear
          </button>
        )}
      </form>

      {/* Table */}
      <div className="bg-card rounded-2xl shadow-brand overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-[var(--brand-primary)]/20 border-t-[var(--brand-primary)] rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <p className="text-red-500 font-medium">{error}</p>
            <button onClick={fetchRecords} className="mt-4 text-[var(--brand-primary)] hover:underline text-sm">
              Try again
            </button>
          </div>
        ) : records.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-muted-foreground">No EOC reports found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">Prospect</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">Closer</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">Setter</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">Outcome</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">Cash</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">Revenue</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">Offer</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">Payment</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground whitespace-nowrap">Source</th>
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec._id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4 whitespace-nowrap text-muted-foreground">
                      {rec.dateCallWasTaken
                        ? format(new Date(rec.dateCallWasTaken), "MMM d, yyyy")
                        : rec.submittedAt
                        ? format(new Date(rec.submittedAt), "MMM d, yyyy")
                        : "—"}
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-foreground">{rec.prospectName || "—"}</p>
                        {rec.prospectEmail && (
                          <p className="text-xs text-muted-foreground truncate max-w-[160px]">{rec.prospectEmail}</p>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-foreground">{rec.closerName || "—"}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-foreground">{rec.setterName || "—"}</td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getOutcomeClass(rec.callOutcome)}`}>
                        {rec.callOutcome || "—"}
                      </span>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap font-medium text-foreground">
                      {formatCurrency(rec.cashCollected)}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap font-medium text-foreground">
                      {formatCurrency(rec.revenueGenerated)}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-muted-foreground max-w-[140px] truncate">
                      {rec.offerPitched || "—"}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-muted-foreground">
                      {rec.paymentMethod || "—"}
                      {rec.paymentPlan && rec.paymentPlan !== "N/A" && (
                        <span className="ml-1 text-xs text-muted-foreground/70">({rec.paymentPlan})</span>
                      )}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-muted-foreground">
                      {rec.sourcePlatform || rec.leadSource || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:bg-secondary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <span className="text-sm text-muted-foreground">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:bg-secondary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Form Builder Modal */}
      {formBuilderOpen && <EOCFormBuilderModal onClose={() => setFormBuilderOpen(false)} />}
    </div>
  );
}
